import React, { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/outline';

const RateModeSelector = ({ currentMode = 'ADMIN', onChange }) => {
  const [selectedMode, setSelectedMode] = useState(currentMode);
  const [settings, setSettings] = useState({
    autoSync: false,
    syncFrequency: 'MANUAL'
  });

  const modes = [
    {
      value: 'ADMIN',
      label: 'Admin Control',
      description: 'Manually set and control all rates',
      icon: '🔒',
      color: 'blue'
    },
    {
      value: 'LIVE',
      label: 'Live Market',
      description: 'Auto-sync with real-time market rates',
      icon: '📈',
      color: 'green'
    },
    {
      value: 'INHERIT',
      label: 'Inherit',
      description: 'Use rates from main branch',
      icon: '🔗',
      color: 'purple'
    },
    {
      value: 'HYBRID',
      label: 'Hybrid',
      description: 'Market rates with fixed margins',
      icon: '⚡',
      color: 'orange'
    }
  ];

  const syncFrequencies = [
    { value: 'REALTIME', label: 'Real-time (5 min)' },
    { value: 'HOURLY', label: 'Every Hour' },
    { value: 'DAILY', label: 'Once Daily' },
    { value: 'MANUAL', label: 'Manual Only' }
  ];

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    if (mode === 'LIVE' || mode === 'HYBRID') {
      setSettings(prev => ({ ...prev, autoSync: true }));
    } else {
      setSettings(prev => ({ ...prev, autoSync: false }));
    }
  };

  const handleApply = () => {
    onChange(selectedMode, settings);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {modes.map(mode => {
          const isSelected = selectedMode === mode.value;
          const colors = {
            blue: 'border-blue-500 bg-blue-50',
            green: 'border-green-500 bg-green-50',
            purple: 'border-purple-500 bg-purple-50',
            orange: 'border-orange-500 bg-orange-50'
          };

          return (
            <button
              key={mode.value}
              onClick={() => handleModeChange(mode.value)}
              className={`
                p-4 rounded-lg border-2 transition-all
                ${isSelected 
                  ? colors[mode.color] 
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <div className="text-2xl mb-2">{mode.icon}</div>
              <div className="font-semibold">{mode.label}</div>
              <div className="text-xs text-gray-600 mt-1">{mode.description}</div>
            </button>
          );
        })}
      </div>

      {(selectedMode === 'LIVE' || selectedMode === 'HYBRID') && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <h4 className="font-semibold flex items-center">
            <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-500" />
            Sync Settings
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="autoSync"
                checked={settings.autoSync}
                onChange={(e) => setSettings(prev => ({ ...prev, autoSync: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="autoSync" className="ml-2 text-sm">
                Enable automatic synchronization
              </label>
            </div>

            {settings.autoSync && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sync Frequency
                </label>
                <select
                  value={settings.syncFrequency}
                  onChange={(e) => setSettings(prev => ({ ...prev, syncFrequency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {syncFrequencies.map(freq => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedMode === 'INHERIT' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-800">
            <strong>Note:</strong> This branch will automatically use rates from the main branch. 
            Any rate changes at the main branch will be reflected here immediately.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleApply}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Apply Mode
        </button>
      </div>
    </div>
  );
};

export default RateModeSelector;